import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import isEmpty from 'is-empty';
import { Typography } from '@material-ui/core';
import { useGlobal } from '@rcg/ui-react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { Banner } from '@rcg/ui-react/lib/components';
import { FormLogin, FormFutureCruiseCredit } from '../../containers';
import { Header, JiraIssueCollector } from '../../components';
import { useStyles } from './index.style';
import { SUBMIT_FUTURE_CRUISE_CREDITS } from '../../graphql/mutations';
import {
  RETRIEVE_BOOKING_FCC,
  CURRENT_BOOKING_FCC,
  GET_SHIPS,
  IS_LOGGED_IN,
  LAUNCH_DARKLY_FLAGS,
} from '../../graphql/queries';
import { ReactComponent as Share } from '../../assets/svg/share.svg';
import defaultNotifications from '../index.defaults';
import Loader from '../../components/Loader/index';
import { LDUser } from '../../commons/ld-user';
import {
  isGuestInfoComplete,
  getGraphQLError,
  BOOKING_TOKEN,
  ERROR_BOOKING_INVALID_STATUS,
  ERROR_BOOKING_INVALID_TYPE,
  ERROR_BOOKING_FCC_INCOMPLETE,
  SUCCESS_BOOKING_FCC_REDMPTION,
  UNSUCCESSFUL_BOOKING_FCC_REDEMPTION,
  INTERNAL_SERVER_ERROR,
} from '../../commons';

import {
  INTERACTIONS,
  trackFccInteraction,
  trackSubmitFccInteraction,
  trackPageView,
} from '../../commons/tracking/fcc-tracking';
import { FCC_ACTIVE, FCC_SUCCES_NOT_ACTIVE } from '../../commons/constants';
import { notificationColor, notificationIcon, safeDecode } from '../../commons/utils';

const buildDescription = (key, renderContent) => {
  const hanldleResponseObj =
    defaultNotifications[key] || defaultNotifications[UNSUCCESSFUL_BOOKING_FCC_REDEMPTION];
  return renderContent(
    hanldleResponseObj.description.key,
    hanldleResponseObj.description.defaultValue,
  );
};

const handleFccSuccesResponse = (response, renderContent) => {
  const { submitFutureCruiseCredits } = response;
  const hanldleResponseObj = defaultNotifications[SUCCESS_BOOKING_FCC_REDMPTION];
  let messageDescription = '';
  let buildMessageDescription = '';
  let counterActiveFcc = 0;
  let counterErrorFcc = 0;
  submitFutureCruiseCredits.forEach((data) => {
    const { type, certificate } = data;
    messageDescription = buildDescription(type, renderContent);
    buildMessageDescription += `FCC ${certificate}: ${messageDescription}<br>`;
    if (type !== FCC_ACTIVE) {
      counterErrorFcc += 1;
      hanldleResponseObj.title = defaultNotifications[FCC_SUCCES_NOT_ACTIVE].title;
      hanldleResponseObj.type = 'error';
    } else {
      counterActiveFcc += 1;
    }
  });
  if (counterActiveFcc > 0 && counterErrorFcc > 0) {
    hanldleResponseObj.title.key = 'fccWarningTitle';
    hanldleResponseObj.title.defaultValue = `Some of the FCCs can't be redeemed:`;
    hanldleResponseObj.type = 'warning';
  }
  hanldleResponseObj.description = buildMessageDescription;
  return hanldleResponseObj;
};

const handleFccErrors = (graphQLErrors, renderContent, fccData) => {
  const hanldleErrorObj = defaultNotifications[UNSUCCESSFUL_BOOKING_FCC_REDEMPTION];
  let errorDescription = '';
  let buildErrorDescription = '';
  graphQLErrors.forEach((error, index) => {
    const code = error.extensions?.code;
    errorDescription = buildDescription(code, renderContent);
    buildErrorDescription += `FCC ${fccData[index]}: ${errorDescription}<br>`;
  });
  hanldleErrorObj.description = buildErrorDescription;
  return hanldleErrorObj;
};

const FutureCruiseCredit = () => {
  document.body.style.backgroundColor = 'white';
  const GLOBALS = useGlobal();
  const [excecuteLaunchDarklyFlag, { data: ldFlagsData, loading: loadingLdFlags }] = useLazyQuery(
    LAUNCH_DARKLY_FLAGS,
    { fetchPolicy: 'cache-first' },
  );
  const { renderContent, data } = GLOBALS;
  const [headerState, headerFunc] = useState({ isPanelOpen: false });
  const [initialHeaderDes, setInitialHeaderDes] = useState(true);
  const [displayLoader, setDisplayLoader] = useState(false);
  const [propNotification, setNotification] = useState({
    description: '',
    open: false,
    title: '',
    type: '',
  });
  const [bookingRetrieved, setBookingRetrieved] = useState(false);
  const [guestCount, setGuestCount] = useState(0);
  const [bookingIsCompleted, setBookingIsCompleted] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [fccData, setFccData] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [jiraWidget, setJiraWidget] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [retrievedToken, setToken] = useState(null);
  const [retreiveBookingData, setRetrieveBooking] = useState(null);
  const currency = useRef();
  const css = useStyles();

  const headerDescription = initialHeaderDes
    ? {
        bundleName: 'formCruiseCreditSubmissionDescription',
        default:
          'Have you received a Future Cruise Credit? Enter your booking information to verify if this booking is eligible.',
      }
    : {
        bundleName: 'formCruiseCreditDescription',
        default:
          "Please enter the cruise credit codes you'd like to apply toward your reservation.",
      };

  const { loading: shipSailingLoading, data: shipSilingData } = useQuery(GET_SHIPS);
  const isBookingSupported = (booking) => {
    const { allowedBookingTypes } = ldFlagsData.ldFlags['enable-cruise-booking-types'];
    setJiraWidget(ldFlagsData?.ldFlags['enable-jira-widget']);
    let validType = true;

    if (
      allowedBookingTypes.length === 0 ||
      (allowedBookingTypes[0] !== '*' &&
        booking.type &&
        !allowedBookingTypes.includes(booking.type))
    )
      validType = false;
    return validType;
  };

  const errorType = (res) => {
    const trackingParams = {
      bookingPassengers: res.guests.length,
      fccrResChannel: res.type,
      ...loginData,
    };
    if (res?.status === 'CANCELLED') return ERROR_BOOKING_INVALID_STATUS;
    if (!isBookingSupported(res)) {
      trackFccInteraction(INTERACTIONS.ERROR_TRAVEL_AGENCY, trackingParams);
      return ERROR_BOOKING_INVALID_TYPE;
    }
    if (!isGuestInfoComplete(res)) {
      trackFccInteraction(INTERACTIONS.ERROR_EXPRESS_CHECKOUT, trackingParams);
      return ERROR_BOOKING_FCC_INCOMPLETE;
    }
    return null;
  };

  const buildURLDasboard = () => {
    const fullUrl = window.location.href;
    const fccBaseURL = 'reservation/';
    const clearFCCUrl = fullUrl.substr(0, fullUrl.lastIndexOf(fccBaseURL) + fccBaseURL.length);
    return `${clearFCCUrl}dashboard`;
  };

  const handleNotification = (notificationObj, description) => {
    const regx = /{phone_number}/g;
    const options = {
      findReplace: [
        {
          find: regx,
          replace: `<a class="message__description__link" href="tel:${data.phoneNumber}">${data.phoneNumber}</a>`,
        },
        {
          find: '{path_dashboard}',
          replace: buildURLDasboard(),
        },
      ],
    };
    setNotification({
      description:
        typeof description !== 'string'
          ? renderContent(
              notificationObj.description.key,
              notificationObj.description.defaultValue,
              options,
            )
          : description.replace(regx, retreiveBookingData?.country?.phoneNumber[0]),
      open: true,
      title: renderContent(notificationObj.title.key, notificationObj.title.defaultValue),
      type: notificationObj.type,
    });
  };

  const onLogin = (booking) => {
    if (!bookingRetrieved) {
      setBookingRetrieved(true);
      const isBookingComplete = isGuestInfoComplete(booking);
      if (isBookingComplete) {
        setGuestCount(booking.guests.length);
        if (booking.guests && booking.guests[0]) {
          setShowEmailInput(isEmpty(booking.guests[0].email));
        }
        setInitialHeaderDes(false);
      }
      setBookingIsCompleted(isBookingComplete);
    }
  };
  const { data: loggedInData } = useQuery(IS_LOGGED_IN, { fetchPolicy: 'no-cache' });
  const skipCurrentBookingQuery = !(loggedInData?.isLoggedIn && !bookingRetrieved && !resetForm);
  const {
    loading: currentBookingLoading,
    data: currentBookingData,
    error: currentBookingError,
  } = useQuery(CURRENT_BOOKING_FCC, {
    skip: skipCurrentBookingQuery,
  });

  if (!skipCurrentBookingQuery && !currentBookingLoading) {
    const currentBooking = currentBookingData?.currentBooking;
    setResetForm(true);
    if (currentBooking) {
      setTokenData({
        rsvNumber: currentBooking.id,
        lastName: currentBooking.guests[0].lastName,
        ship: currentBooking.sailing.itinerary.ship.code,
        sailDate: currentBooking.sailing.sailDate,
      });
      onLogin(currentBooking);
    } else {
      const errorObj = getGraphQLError(currentBookingError);
      handleNotification(errorObj);
    }
  }

  useEffect(() => {
    if (propNotification.open) {
      window.scrollTo(0, 0);
    }
  }, [propNotification]);

  useEffect(() => {
    if (ldFlagsData) {
      const fccNewService = ldFlagsData.ldFlags['enable-fcc-real-time-redemption'];
      sessionStorage.setItem('FCC_REAL_TIME', fccNewService);
      const err = errorType(retreiveBookingData);
      if (!err) {
        sessionStorage.setItem(BOOKING_TOKEN, safeDecode(retrievedToken));
        currency.current = retreiveBookingData?.price?.currencyCode;
        trackFccInteraction(INTERACTIONS.SUCCESS, {
          bookingPassengers: retreiveBookingData.guests.length,
          fccrResChannel: retreiveBookingData.type,
        });
        onLogin(retreiveBookingData);
      } else {
        if (err === ERROR_BOOKING_FCC_INCOMPLETE) {
          sessionStorage.setItem(BOOKING_TOKEN, safeDecode(retrievedToken));
        }
        handleNotification(defaultNotifications[err]);
      }
    }
    // eslint-disable-next-line
  }, [ldFlagsData])

  const [login, { loading: loadingLogin, data: dataRetrieve, error: errorRetrieve }] = useLazyQuery(
    RETRIEVE_BOOKING_FCC,
    { fetchPolicy: 'cache-first' },
  );
  useEffect(() => {
    if (dataRetrieve && !loadingLogin) {
      const { retrieveBooking } = dataRetrieve;
      const { token, booking } = retrieveBooking;
      setRetrieveBooking(booking);
      setToken(token);
      excecuteLaunchDarklyFlag({
        variables: {
          ldUser: LDUser(),
          officeID: booking?.country?.office?.code,
        },
      });
    }
    if (errorRetrieve) {
      const errorObj = getGraphQLError(errorRetrieve);
      const { trackingMsg } = errorObj;
      if (trackingMsg === defaultNotifications[INTERNAL_SERVER_ERROR].trackingMsg) {
        trackFccInteraction(INTERACTIONS.ERROR_TECNICAL_ISSUES, loginData);
      } else {
        trackFccInteraction(INTERACTIONS.ERROR_WRONG_NUMBER, loginData);
      }
      handleNotification(errorObj);
    }
    // eslint-disable-next-line
  }, [dataRetrieve, loadingLogin, errorRetrieve])
  const onLoginClick = (loginClickData) => {
    setResetForm(false);
    setNotification({ open: false });
    setLoginData(loginClickData.variables);
    trackFccInteraction(INTERACTIONS.SUBMITTED, loginClickData.variables);
    login(loginClickData);
  };

  const [submitCredit, { loading: loadingCredit }] = useMutation(SUBMIT_FUTURE_CRUISE_CREDITS, {
    onCompleted: (result) => {
      trackSubmitFccInteraction(INTERACTIONS.FCCR_SUCCESS, {
        result,
        bookingPassengers: guestCount,
      });
      const fccRealTime = sessionStorage.getItem('FCC_REAL_TIME');
      const fccNotificationObject =
        fccRealTime === 'true'
          ? handleFccSuccesResponse(result, renderContent)
          : defaultNotifications[SUCCESS_BOOKING_FCC_REDMPTION];
      handleNotification(fccNotificationObject, fccNotificationObject.description);

      setResetForm(true);
      setTokenData({});
      setBookingRetrieved(false);
      sessionStorage.clear();
      setInitialHeaderDes(true);
    },
    onError: ({ graphQLErrors }) => {
      let fccError;
      const fccRealTime = sessionStorage.getItem('FCC_REAL_TIME');
      if (graphQLErrors && graphQLErrors.length > 0) {
        trackSubmitFccInteraction(INTERACTIONS.FCCR_ERROR, {
          result: {
            fccCodesRejected: fccData,
          },
          bookingPassengers: guestCount,
        });
        fccError =
          fccRealTime === 'true'
            ? handleFccErrors(graphQLErrors, renderContent, fccData)
            : defaultNotifications[UNSUCCESSFUL_BOOKING_FCC_REDEMPTION];
      } else {
        trackSubmitFccInteraction(INTERACTIONS.FCCR_ERROR, {
          bookingPassengers: guestCount,
        });
        fccError = defaultNotifications[UNSUCCESSFUL_BOOKING_FCC_REDEMPTION];
      }
      handleNotification(fccError, fccError.description);
    },
  });
  const submitCreditClick = (creditData) => {
    setNotification({ open: false });
    setFccData(creditData.variables.codes);
    submitCredit(creditData);
  };

  const cancelCreditClick = () => {
    setInitialHeaderDes(true);
    setResetForm(true);
    setTokenData({});
    setBookingRetrieved(false);
    sessionStorage.clear();
  };

  useEffect(() => {
    setDisplayLoader(currentBookingLoading || loadingLogin || loadingCredit || shipSailingLoading);
  }, [currentBookingLoading, loadingLogin, loadingCredit, shipSailingLoading]);

  const searchSailingsDates = shipSilingData && shipSilingData.searchSailings;
  const shipsCodes = shipSilingData && shipSilingData.ships;

  const shareIconComponent = (
    <>
      <Share className="share__icon" />
      <Typography className="share__label">
        {renderContent('headerBookingAmendShareLabel', 'Share')}
      </Typography>
    </>
  );

  return (
    <div className={css.root}>
      <Helmet>
        <title>
          {renderContent('formBookingAmendPageTitle', 'Royal Caribbean Cruises: Reservation')}
        </title>
      </Helmet>
      <Header
        className="header"
        title={renderContent('headerBookingAmendWelcome', 'Welcome!')}
        backLabel={renderContent('headerBookingAmendBackLabel', 'Back')}
        headerFunc={headerFunc}
        headerState={headerState}
        trackingFn={trackPageView}
        primaryLogoutLabel={renderContent(`headerCruiseCreditExitLabel`, `Exit`)}
        secondLogoutLabel={renderContent(`formCruiseCreditReservationLabel`, `Reservation`)}
        shareIcon={shareIconComponent}
        currency={currency.current}
      />
      {jiraWidget && <JiraIssueCollector />}
      {propNotification.open && (
        <div className={css.bannerWrapper}>
          <Banner
            {...propNotification}
            color={notificationColor(propNotification.type)}
            icon={notificationIcon(propNotification.type)}
          />
        </div>
      )}
      <FormLogin
        login={onLoginClick}
        formTitle={renderContent('formCruiseCreditTitle', 'Future Cruise Credit Redemption')}
        formDescription={renderContent(headerDescription.bundleName, headerDescription.default)}
        disabledAllFields={bookingRetrieved}
        hideSubmitButton={bookingRetrieved}
        reinitializeForm={resetForm}
        tokenData={tokenData}
        searchSailings={searchSailingsDates || undefined}
        ships={shipsCodes || undefined}
      />
      {bookingRetrieved && bookingIsCompleted && (
        <FormFutureCruiseCredit
          submit={submitCreditClick}
          cancel={cancelCreditClick}
          numberOfGuests={guestCount}
          showEmailInput={showEmailInput}
          status={retreiveBookingData?.status}
        />
      )}
      <Loader display={displayLoader || loadingLdFlags} />
      <div className={css.leftPadding}>{sessionStorage.getItem('eslEnv')}</div>
    </div>
  );
};

export default FutureCruiseCredit;
